import axios from 'axios';
import {toast} from 'react-toastify';
import {call, delay, ForkEffect, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ActionCreators} from 'redux-undo';
import i18n, {type TypedTranslation} from '../../i18n';
import {ISlide, SlideWithoutElements} from '../../incase-domain/slide';
import {deleteSlides, updateSlidesWithoutElements} from '../../service/slides';
import {areSlidesDifferent} from '../../utils/slide';
import {mergeSlidePreviewIntoList, withSlidePreviewCacheBust} from '../../utils/slidePreviewState';
import {swapArrayElement, swapArrayIndices} from '../../utils/utils';
import {
    updateSelectedPresentationAction,
    updateSelectedPresentationSlideIndexListAction,
    updateSelectedPresentationSlidesAction,
} from '../actions/presentation';
import {
    ADD_EMITTED_SLIDE_ACTION,
    BRING_ELEMENT_TO_FRONT,
    COPY_EMITTED_SLIDE_ACTION,
    DELETE_SLIDES,
    IMPORT_EMITTED_SLIDE_ACTION,
    REMOVE_EMITTED_SLIDE_ACTION,
    SEND_ELEMENT_TO_BACK,
    setEmittedSlideAction,
    setHighlightedElementIndex,
    SWAP_EMITTED_SLIDE_ACTION,
    UPDATE_EMITTED_SLIDE_ACTION,
    UPDATE_EMITTED_SLIDE_PREVIEW_ACTION,
    UPDATE_SLIDES_WITHOUT_ELEMENTS_ACTION,
    updateSelectedSlideAction,
} from '../actions/slide';
import {
    clearSlidePreviewPendingAction,
    MARK_SLIDE_PREVIEW_PENDING,
    MARK_SLIDE_PREVIEWS_PENDING,
    updateSelectedSlidePreviewsAction,
} from '../actions/slide.preview';
import {IMarkSlidePreviewPendingAction, IMarkSlidePreviewsPendingAction} from '../actions/types/slide.preview.types';
import {startPresUpdateSpinnerAction, stopPresUpdateSpinnerAction} from '../actions/spinner';
import {
    IAddEmittedSlideAction,
    IBringElementToFrontAction,
    ICopyEmittedSlideAction,
    IDeleteSlidesAction,
    IImportEmittedSlideAction,
    IRemoveEmittedSlideAction,
    ISendElementToBackAction,
    ISwapEmittedSlideAction,
    IUpdateEmittedSlideAction,
    IUpdateEmittedSlidePreviewAction,
    IUpdateSlidesWithoutElementsAction,
} from '../actions/types/slide.types';
import {AppState} from '../reducer';

export function* updateSlidesWithoutElementsSaga(action: IUpdateSlidesWithoutElementsAction): unknown {
    if (!action.slides.length) return;
    yield put(startPresUpdateSpinnerAction(true));
    try {
        const state: AppState = yield select();
        const {selectedPresentation} = state.presentationReducer;

        const slidesWithoutElements: SlideWithoutElements[] = action.slides.map(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ({elements, ...rest}) => rest
        );
        const response: SlideWithoutElements[] = yield call(updateSlidesWithoutElements, selectedPresentation._id, slidesWithoutElements);

        const updatedById = new Map<string, ISlide>();
        action.slides.forEach((slide) => {
            const updated = response.find((r) => r._id === slide._id);
            updatedById.set(slide._id, {...slide, ...updated});
        });

        const updatedSlideList = selectedPresentation.slides.map((s) => {
            const updated = updatedById.get(s._id);
            return updated ? {...s, ...updated, elements: s.elements} : s;
        });
        yield put(updateSelectedPresentationSlidesAction(updatedSlideList));

        const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;
        const updatedSelected = updatedById.get(selectedSlide._id);
        if (updatedSelected) {
            yield put(
                updateSelectedSlideAction(
                    {...selectedSlide, ...updatedSelected, elements: selectedSlide.elements},
                    selectedSlideIndex,
                    false
                )
            );
        } else {
            yield put(updateSelectedSlideAction(selectedSlide, selectedSlideIndex, state.slideReducer.present.isSlideModified));
        }
    } catch (error) {
        const messageKey =
            axios.isAxiosError(error) && typeof error.response?.data?.message === 'string'
                ? error.response.data.message
                : 'toasts.errorOccurred';
        yield call(toast.error, i18n.t(messageKey as TypedTranslation));
    } finally {
        yield put(stopPresUpdateSpinnerAction());
    }
}

export function* deleteSlidesSaga(action: IDeleteSlidesAction): unknown {
    const {slides} = action;
    if (!slides.length) return;

    yield put(startPresUpdateSpinnerAction(true));

    const initialState: AppState = yield select();
    const presentationId = initialState.presentationReducer.selectedPresentation._id;

    const response: {removed: boolean; removedIds: string[]} | undefined = yield call(
        deleteSlides,
        presentationId,
        slides.map((s) => s._id)
    );

    if (!response || !response.removed) {
        yield put(stopPresUpdateSpinnerAction());
        yield call(toast.error, i18n.t('toasts.deleteSlideError'));
        return;
    }

    const removedIds = new Set(response.removedIds);

    const state: AppState = yield select();
    const {selectedSlide} = state.slideReducer.present;
    const {selectedPresentation} = state.presentationReducer;
    const currentSlides = selectedPresentation.slides;

    const remainingSlides = currentSlides.filter((s) => !removedIds.has(s._id));
    const remainingIndexList = selectedPresentation.slideIndexList.filter((id) => !removedIds.has(id));

    if (removedIds.has(selectedSlide._id) && remainingSlides.length > 0) {
        const firstRemovedIndex = currentSlides.findIndex((s) => removedIds.has(s._id));
        const newSelectedSlideIndex = Math.max(0, firstRemovedIndex - 1);
        yield put(updateSelectedSlideAction(remainingSlides[newSelectedSlideIndex], newSelectedSlideIndex, false));
    }

    yield put(updateSelectedPresentationSlideIndexListAction(remainingIndexList));
    yield put(updateSelectedPresentationSlidesAction(remainingSlides));
    yield put(stopPresUpdateSpinnerAction());
    yield put(ActionCreators.clearHistory());

    yield call(toast.success, i18n.t('toasts.deleteSlideSuccess'));
}

export function* addEmittedSlideSaga(action: IAddEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    if (!action.slides || !action.slideIndexList) return;

    const isAlreadyUpdated =
        new Set([...selectedPresentation.slideIndexList, ...action.slideIndexList]).size === selectedPresentation.slideIndexList.length;
    if (isAlreadyUpdated) return;

    yield put(startPresUpdateSpinnerAction(true));

    const actionSlide = action.slides[0];
    const index = action.slideIndexList.indexOf(actionSlide._id || '');
    const newSelectedPresentationSlides = [...selectedPresentation.slides];
    newSelectedPresentationSlides.splice(index, 0, actionSlide);

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(action.slideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* removeEmittedSlideSaga(action: IRemoveEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlide} = state.slideReducer.present;

    yield put(startPresUpdateSpinnerAction(true));

    const newSelectedPresentationSlides = selectedPresentation.slides.filter((selectedSlide) => selectedSlide._id !== action.slideId);
    const newSelectedPresentationSlideIndexList = selectedPresentation.slideIndexList.filter(
        (selectedSlideIndex) => selectedSlideIndex !== action.slideId
    );

    if (selectedSlide._id === action.slideId) {
        const index = selectedPresentation.slideIndexList.indexOf(action.slideId || '');
        if (index === 0) {
            const selectedSlide = selectedPresentation.slides[index + 1];
            yield put(updateSelectedSlideAction(selectedSlide, index + 1, false));
        } else {
            const selectedSlide = selectedPresentation.slides[index - 1];
            yield put(updateSelectedSlideAction(selectedSlide, index - 1, false));
        }
    }

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(newSelectedPresentationSlideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* copyEmittedSlideSaga(action: ICopyEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    yield put(startPresUpdateSpinnerAction(true));

    const copiedSlideIndex = selectedPresentation.slideIndexList.findIndex((id) => id === action.slideId);
    const newSlideIndexList = [...selectedPresentation.slideIndexList];
    newSlideIndexList.splice(copiedSlideIndex + 1, 0, action.slide?._id);

    const newSelectedPresentationSlides = [...selectedPresentation.slides];
    newSelectedPresentationSlides.splice(copiedSlideIndex + 1, 0, action.slide);

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(newSlideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* updateEmittedSlideSaga(action: IUpdateEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;
    const {selectedSlide} = state.slideReducer.present;

    yield put(startPresUpdateSpinnerAction(true));

    if (selectedSlide._id === action.slide._id && areSlidesDifferent(selectedSlide, action.slide)) {
        yield put(setEmittedSlideAction(action.slide));
    } else if (selectedPresentation.slideIndexList.length > 1) {
        const updatesSlideIndex = selectedPresentation.slides.findIndex((slide) => slide._id === action.slide?._id);
        const updatedSlideList = swapArrayElement(selectedPresentation.slides, updatesSlideIndex, action.slide);

        yield put(updateSelectedPresentationSlidesAction(updatedSlideList));
        yield put(updateSelectedSlidePreviewsAction(selectedPresentationSlidePreviews));
    }
    yield put(stopPresUpdateSpinnerAction());
}

export function* updateEmittedSlidePreviewSaga(action: IUpdateEmittedSlidePreviewAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentationSlidePreviews} = state.presentationReducer;
    const incoming = action.slidePreview;
    const incomingId = incoming.slideId || (incoming as {_id?: string})._id || '';
    const slidePreview = {
        ...withSlidePreviewCacheBust(incoming),
        slideId: String(incomingId),
    };
    const updatedSlidePreviewList = mergeSlidePreviewIntoList(selectedPresentationSlidePreviews, slidePreview);
    if (updatedSlidePreviewList !== selectedPresentationSlidePreviews) {
        yield put(updateSelectedSlidePreviewsAction(updatedSlidePreviewList));
    } else if (slidePreview.slideId) {
        yield put(clearSlidePreviewPendingAction(slidePreview.slideId));
    }
}

const SLIDE_PREVIEW_PENDING_TIMEOUT_MS = 10000;
const SLIDE_PREVIEWS_PENDING_TIMEOUT_MS = 180000;

export function* expireSlidePreviewPendingSaga(action: IMarkSlidePreviewPendingAction): unknown {
    yield delay(SLIDE_PREVIEW_PENDING_TIMEOUT_MS);
    yield put(clearSlidePreviewPendingAction(action.slideId));
}

export function* expireSlidePreviewsPendingSaga(action: IMarkSlidePreviewsPendingAction): unknown {
    yield delay(SLIDE_PREVIEWS_PENDING_TIMEOUT_MS);

    action.slideIds.forEach((slideId) => {
        put(clearSlidePreviewPendingAction(slideId));
    });
}

export function* importEmittedSlideSaga(action: IImportEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    yield put(startPresUpdateSpinnerAction(true));

    yield put(
        updateSelectedPresentationAction({
            ...action.presentation,
            slides: [...selectedPresentation.slides, ...action.slides],
        })
    );

    yield put(stopPresUpdateSpinnerAction());
}

export function* swapEmittedSlideSaga(action: ISwapEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;
    yield put(startPresUpdateSpinnerAction(true));

    const comparator = (a: ISlide, b: ISlide): number => {
        const idA = action.slideIndexList.indexOf(a._id);
        const idB = action.slideIndexList.indexOf(b._id);
        return idA - idB;
    };

    const sortedSlides = selectedPresentation.slides.sort(comparator);

    const updatedPresentation = {
        ...selectedPresentation,
        slideIndexList: action.slideIndexList,
        slides: sortedSlides,
    };
    yield put(updateSelectedPresentationAction(updatedPresentation));
    yield put(updateSelectedSlidePreviewsAction(selectedPresentationSlidePreviews));
    yield put(stopPresUpdateSpinnerAction());
}

export function* bringElementToFrontSaga(action: IBringElementToFrontAction): unknown {
    const state: AppState = yield select();
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    const newIndex = selectedSlide.elements.length - 1 || 0;
    const newElements = swapArrayIndices(selectedSlide.elements, newIndex, action.elementIndex);

    yield put(updateSelectedSlideAction({...selectedSlide, elements: newElements}, selectedSlideIndex, true));
    yield put(setHighlightedElementIndex(newIndex));
}

export function* sendElementToBackSaga(action: ISendElementToBackAction): unknown {
    const state: AppState = yield select();
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    const newElements = swapArrayIndices(selectedSlide.elements, 0, action.elementIndex);

    yield put(updateSelectedSlideAction({...selectedSlide, elements: newElements}, selectedSlideIndex, true));
    yield put(setHighlightedElementIndex(0));
}

export function* watchSlideSaga(): Generator<ForkEffect<never>, void> {
    yield takeLatest(DELETE_SLIDES, deleteSlidesSaga);
    yield takeLatest(UPDATE_SLIDES_WITHOUT_ELEMENTS_ACTION, updateSlidesWithoutElementsSaga);
    yield takeLatest(ADD_EMITTED_SLIDE_ACTION, addEmittedSlideSaga);
    yield takeLatest(REMOVE_EMITTED_SLIDE_ACTION, removeEmittedSlideSaga);
    yield takeLatest(COPY_EMITTED_SLIDE_ACTION, copyEmittedSlideSaga);
    yield takeLatest(UPDATE_EMITTED_SLIDE_ACTION, updateEmittedSlideSaga);
    yield takeEvery(UPDATE_EMITTED_SLIDE_PREVIEW_ACTION, updateEmittedSlidePreviewSaga);
    yield takeEvery(MARK_SLIDE_PREVIEW_PENDING, expireSlidePreviewPendingSaga);
    yield takeLatest(MARK_SLIDE_PREVIEWS_PENDING, expireSlidePreviewsPendingSaga);
    yield takeLatest(IMPORT_EMITTED_SLIDE_ACTION, importEmittedSlideSaga);
    yield takeLatest(SWAP_EMITTED_SLIDE_ACTION, swapEmittedSlideSaga);
    yield takeLatest(BRING_ELEMENT_TO_FRONT, bringElementToFrontSaga);
    yield takeLatest(SEND_ELEMENT_TO_BACK, sendElementToBackSaga);
}
